import {
  BuildingContact,
  BuildingInfoWithStats,
  CompanyAllBuildingsMatch,
  UserAllBuildingsMatch,
  searchUsersInAllBuildings,
  searchCompaniesInAllBuildings,
  useAllBuildingInfo,
} from "@/api";
import { SelectRow } from "@app-components";
import AuthProvider from "@/providers/AuthProvider";
import SupplierAdminsBackfillButton from "@/components/building_overview/SupplierAdminsBackfillButton";
import whiteLogo from "@/assets/whiteLogo.svg";
import { APP_DOMAIN, notifyApiError } from "@/lib";
import { useLoggedInUser, useSnackbarStore } from "@/store/zustandStore";
import { useTranslation } from "react-i18next";
import styles from "@/styles/BuildingOverview.module.scss";
import { Building } from "@/types";
import {
  Link,
  Navigate,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CSSProperties, Suspense, useEffect, useMemo, useState } from "react";
import { z } from "zod";

const TABS = ["buildings", "users", "companies"] as const;
type Tab = (typeof TABS)[number];

// Tab, submitted searches and building filters live in the URL so a view can
// be shared and survives a reload.
const overviewSearchSchema = z.object({
  tab: z.enum(TABS).optional().catch(undefined),
  user: z.string().optional().catch(undefined),
  company: z.string().optional().catch(undefined),
  filter: z.string().optional().catch(undefined),
  features: z.array(z.string()).optional().catch(undefined),
  sort: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/building_overview")({
  validateSearch: (search) => overviewSearchSchema.parse(search),
  component: () => (
    <AuthProvider>
      <Suspense fallback={<LoadingSkeleton />}>
        <RouteComponent />
      </Suspense>
    </AuthProvider>
  ),
});

function LoadingSkeleton() {
  return (
    <div
      className={styles.boRoot}
      style={{ height: "100%", width: "100%", overflowY: "auto" }}
    >
      <div className={styles.boTopbar}>
        <div className={styles.boTopbarLeft}>
          <img
            src={whiteLogo}
            alt="Welcome Workdays"
            className={styles.boLogo}
          />
          <span className={styles.boTopbarDivider} aria-hidden="true" />
          <div>
            <div className={styles.boEyebrow}>Portal · admin</div>
            <h1 className={styles.boPageTitle}>Portfolio overview</h1>
          </div>
        </div>
        <div
          className={styles.boSkel}
          style={{ width: 240, height: 40, borderRadius: 999 }}
        />
      </div>
      <div className={styles.boSummary}>
        <div className={styles.boSummaryTotal}>
          <div className={styles.boSkel} style={{ width: 70, height: 44 }} />
          <div
            className={styles.boSkel}
            style={{ width: 80, height: 12, marginTop: 8 }}
          />
        </div>
        <div className={styles.boSummaryDivider} />
        <div className={styles.boSummaryChips}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={styles.boSkel}
              style={{ height: 56, borderRadius: 12 }}
            />
          ))}
        </div>
      </div>
      <div className={styles.boFilterbar}>
        <div
          className={styles.boSkel}
          style={{ flex: 1, maxWidth: 480, height: 40, borderRadius: 12 }}
        />
        <div style={{ flex: 1 }} />
        <div
          className={styles.boSkel}
          style={{ width: 180, height: 40, borderRadius: 999 }}
        />
      </div>
      <div className={styles.boTableWrap}>
        <table className={styles.boTable}>
          <thead>
            <tr>
              <th>Building</th>
              {FEATURES.map((f) => (
                <th key={f.key as string} className={styles.feat}>
                  <div className={styles.boFeatHead}>
                    <span
                      className={styles.boFeatHeadDot}
                      style={{ background: f.color }}
                    />
                    <span style={{ fontWeight: 600 }}>{f.name}</span>
                  </div>
                </th>
              ))}
              <th>Companies</th>
              <th>Users</th>
              <th>Contacts</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }).map((_, r) => (
              <tr key={r}>
                <td>
                  <div
                    className={styles.boSkel}
                    style={{ width: "60%", height: 14 }}
                  />
                  <div
                    className={styles.boSkel}
                    style={{ width: "40%", height: 11, marginTop: 6 }}
                  />
                </td>
                {FEATURES.map((f) => (
                  <td
                    key={f.key as string}
                    className={styles.boFeatCell}
                    style={{ textAlign: "center" }}
                  >
                    <span
                      className={styles.boSkel}
                      style={{
                        display: "inline-block",
                        width: 30,
                        height: 30,
                        borderRadius: 8,
                      }}
                    />
                  </td>
                ))}
                {[0, 1].map((i) => (
                  <td key={i}>
                    <div
                      className={styles.boSkel}
                      style={{ width: 28, height: 12 }}
                    />
                  </td>
                ))}
                <td>
                  <div style={{ display: "flex", gap: 6 }}>
                    <span
                      className={styles.boSkel}
                      style={{ width: 20, height: 20, borderRadius: 999 }}
                    />
                    <div
                      className={styles.boSkel}
                      style={{ width: 80, height: 12, alignSelf: "center" }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type BuildingWithId = BuildingInfoWithStats;

type FeatureDef = {
  key: keyof Building;
  name: string;
  icon: string;
  color: string;
  soft: string;
  link: string;
};

const FEATURES: FeatureDef[] = [
  {
    key: "bookingEnabled",
    name: "Booking",
    icon: "date_range",
    color: "#5BA0E2",
    soft: "rgba(18, 99, 173, 0.24)",
    link: "settings/booking",
  },
  {
    key: "hasWelcomeFood",
    name: "Food",
    icon: "restaurant",
    color: "#E3A06F",
    soft: "rgba(211, 128, 64, 0.22)",
    link: "settings/food",
  },
  {
    key: "hasWelcomeSnack",
    name: "Snack",
    icon: "cookie",
    color: "#ECD865",
    soft: "rgba(216, 195, 46, 0.18)",
    link: "settings/food",
  },
  {
    key: "hasCommunication",
    name: "Comms",
    icon: "campaign",
    color: "#B782D4",
    soft: "rgba(150, 88, 183, 0.20)",
    link: "settings/communication",
  },
  {
    key: "accessControlSystemIntegration",
    name: "Access",
    icon: "key",
    color: "#ED7A79",
    soft: "rgba(221, 79, 78, 0.20)",
    link: "settings/access",
  },
];

const cls = (...xs: (string | false | undefined | null)[]) =>
  xs.filter(Boolean).join(" ");

function MIcon({
  name,
  size = 18,
  fill = 0,
  style,
}: {
  name: string;
  size?: number;
  fill?: 0 | 1;
  style?: CSSProperties;
}) {
  return (
    <span
      className={styles.boMsi}
      style={{
        fontSize: size,
        fontVariationSettings: `'FILL' ${fill}, 'wght' 400, 'GRAD' 0, 'opsz' 24`,
        ...style,
      }}
    >
      {name}
    </span>
  );
}

const AVATAR_PALETTE = ["#B782D4", "#5BA0E2", "#6FC56F", "#E3A06F", "#E0E0E0"];
const avatarColor = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++)
    h = (h * 31 + seed.charCodeAt(i)) & 0xffff;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

function RouteComponent() {
  const user = useLoggedInUser();
  // Checked before any data hook runs: the endpoints reject non-wlcm.tech
  // users, so loading first would land them on the error boundary instead.
  if (!user.email.match(/^.*@(?:[a-zA-Z0-9-]+\.)*wlcm\.tech$/)) {
    return <Navigate to="/" />;
  }
  return <Overview />;
}

function Overview() {
  const { t } = useTranslation();
  const { addNotification } = useSnackbarStore();
  const { data: buildings } = useAllBuildingInfo();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // Submitted searches and tab switches get history entries so Back/Forward
  // steps through them; keystroke-level changes (filter text) replace.
  const setSearch = (patch: Partial<typeof search>, replace = false) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace });

  const tab: Tab = search.tab ?? "users";
  const searchString = search.filter ?? "";
  const activeFilters = search.features ?? [];
  const sort = search.sort ?? "name";
  const userQuery = search.user ?? "";
  const companyQuery = search.company ?? "";

  // Inputs are local until submitted; submitting writes the query to the URL,
  // which is what drives the search. They re-sync whenever the URL changes
  // (Back/Forward, a pasted link, "Clear results").
  const [userSearchInput, setUserSearchInput] = useState(userQuery);
  const [companySearchInput, setCompanySearchInput] = useState(companyQuery);
  useEffect(() => setUserSearchInput(userQuery), [userQuery]);
  useEffect(() => setCompanySearchInput(companyQuery), [companyQuery]);

  const {
    data: searchUsers,
    isFetching: isPending,
    error: userSearchError,
  } = useQuery({
    queryKey: ["overviewSearchUsers", userQuery],
    queryFn: () => searchUsersInAllBuildings(userQuery),
    enabled: !!userQuery,
    retry: false,
  });

  const {
    data: companyResults,
    isFetching: companyPending,
    error: companySearchError,
  } = useQuery({
    queryKey: ["overviewSearchCompanies", companyQuery],
    queryFn: () => searchCompaniesInAllBuildings(companyQuery),
    enabled: !!companyQuery,
    retry: false,
  });

  // Notify once per failure; the failed result isn't cached as "no matches".
  useEffect(() => {
    if (userSearchError) notifyApiError(userSearchError, addNotification, t);
  }, [userSearchError]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (companySearchError)
      notifyApiError(companySearchError, addNotification, t);
  }, [companySearchError]); // eslint-disable-line react-hooks/exhaustive-deps

  const all = buildings as BuildingWithId[];

  const buildingNames = useMemo(
    () => new Map(all.map((b) => [b.id, b.displayName || b.id])),
    [all],
  );

  // One row per company, listing every building it occupies.
  const groupedCompanies = useMemo(() => {
    const byId = new Map<
      string,
      {
        buildingTenantId: string;
        buildingTenantName: string;
        orgNumber: string;
        sharryCompanyId?: string;
        buildings: CompanyAllBuildingsMatch[];
      }
    >();
    for (const c of companyResults ?? []) {
      const existing = byId.get(c.buildingTenantId);
      if (existing) existing.buildings.push(c);
      else byId.set(c.buildingTenantId, { ...c, buildings: [c] });
    }
    return Array.from(byId.values());
  }, [companyResults]);

  const filtered = useMemo(() => {
    const q = searchString.toLowerCase();
    const out = all.filter((b) => {
      const matchesQ =
        !q ||
        (b.displayName ?? "").toLowerCase().includes(q) ||
        (b.owner ?? "").toLowerCase().includes(q);
      const matchesFilters = activeFilters.every(
        (k) => !!b[k as keyof Building],
      );
      return matchesQ && matchesFilters;
    });
    const byName = (a: BuildingWithId, b: BuildingWithId) =>
      (a.displayName ?? "").localeCompare(b.displayName ?? "");
    const featCount = (b: BuildingWithId) =>
      FEATURES.reduce((n, f) => n + (b[f.key] ? 1 : 0), 0);
    out.sort((a, b) => {
      switch (sort) {
        case "owner":
          return (a.owner ?? "").localeCompare(b.owner ?? "") || byName(a, b);
        case "features-desc":
          return featCount(b) - featCount(a) || byName(a, b);
        case "features-asc":
          return featCount(a) - featCount(b) || byName(a, b);
        default:
          return byName(a, b);
      }
    });
    return out;
  }, [all, searchString, sort, activeFilters]);

  const toggleFilter = (k: string) =>
    setSearch({
      features: activeFilters.includes(k)
        ? activeFilters.filter((x) => x !== k)
        : [...activeFilters, k],
    });
  const clearFilters = () => setSearch({ features: undefined });

  const count = (key: keyof Building) => filtered.filter((b) => b[key]).length;
  const pct = (n: number, d: number) =>
    d === 0 ? 0 : Math.round((n / d) * 100);

  return (
    <div
      className={styles.boRoot}
      style={{ height: "100%", width: "100%", overflowY: "auto" }}
    >
      <div className={styles.boTopbar}>
        <div className={styles.boTopbarLeft}>
          <img
            src={whiteLogo}
            alt="Welcome Workdays"
            className={styles.boLogo}
          />
          <span className={styles.boTopbarDivider} aria-hidden="true" />
          <div>
            <div className={styles.boEyebrow}>Portal · admin</div>
            <h1 className={styles.boPageTitle}>Portfolio overview</h1>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/" className={cls(styles.boBtn, styles.ghost)}>
            <MIcon name="arrow_back" size={16} />
            Back to portal
          </Link>
          <SupplierAdminsBackfillButton />
        </div>
      </div>

      <div className={styles.boTabs}>
        <SelectRow
          tabs
          hideLabel
          label="Section"
          name="building-overview-tabs"
          value={tab}
          onChange={(v) => setSearch({ tab: v as Tab })}
          options={[
            { value: "buildings", label: "Buildings", icon: "domain" },
            { value: "users", label: "Users", icon: "person_search" },
            { value: "companies", label: "Companies", icon: "work" },
          ]}
        />
      </div>

      {tab === "users" && (
        <div className={styles.boUsearch}>
          <form
            className={styles.boUsearchForm}
            onSubmit={(e) => {
              e.preventDefault();
              setSearch({ user: userSearchInput.trim() || undefined });
            }}
          >
            <div className={styles.boInputWrap} style={{ flex: 1 }}>
              <MIcon
                name="person_search"
                size={18}
                style={{ color: "var(--fg-3)" }}
              />
              <input
                type="text"
                placeholder="Email, name or phone…"
                value={userSearchInput}
                onChange={(e) => setUserSearchInput(e.target.value)}
                autoFocus
              />
              {userSearchInput && (
                <button
                  type="button"
                  className={styles.boInputClear}
                  onClick={() => {
                    setUserSearchInput("");
                    setSearch({ user: undefined });
                  }}
                  aria-label="Clear"
                >
                  <MIcon name="close" size={16} />
                </button>
              )}
            </div>
            <button
              type="submit"
              className={cls(styles.boBtn, styles.primary)}
              disabled={isPending || !userSearchInput}
            >
              {isPending ? "Searching…" : "Search"}
            </button>
          </form>

          {searchUsers && (
            <div className={styles.boUsearchResults}>
              <div className={styles.boUsearchResultsHead}>
                <MIcon name="person" size={16} />
                <strong>
                  {searchUsers.length} user
                  {searchUsers.length === 1 ? "" : "s"}
                </strong>
                <div style={{ flex: 1 }} />
                <button
                  type="button"
                  className={cls(styles.boBtn, styles.ghost, styles.boBtnSm)}
                  onClick={() => setSearch({ user: undefined })}
                >
                  <MIcon name="close" size={14} />
                  Clear results
                </button>
              </div>
              {searchUsers.length === 0 ? (
                <div className={styles.boEmpty}>No users match.</div>
              ) : (
                <table className={styles.boUsearchTable}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Company</th>
                      <th>Buildings &amp; roles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchUsers.map((u) => {
                      // The company is global, so every building row carries
                      // the same one; dedupe in case of stale data.
                      const companies = Array.from(
                        new Map(
                          u.buildings.map((b) => [b.companyId, b.companyName]),
                        ),
                      );
                      return (
                        <tr key={u.userId}>
                          <td>
                            <div>
                              {[u.firstName, u.lastName]
                                .filter(Boolean)
                                .join(" ")}
                              {u.sharryUserId && (
                                <SharryLink
                                  href={`https://welcome.sharryapp.com/portalwx/employee/form/${encodeURIComponent(u.sharryUserId)}`}
                                />
                              )}
                            </div>
                            <div>
                              {u.email}
                              <CopyButton value={u.email} />
                            </div>
                            <div className={cls(styles.boMono, styles.boMuted)}>
                              {u.userId}
                              <CopyButton value={u.userId} />
                            </div>
                          </td>
                          <td className={styles.boMono}>
                            {u.phone}
                            {u.phone && <CopyButton value={u.phone} />}
                          </td>
                          <td>
                            <span
                              className={cls(
                                styles.boTag,
                                u.userActive && styles.boTagOn,
                              )}
                            >
                              {u.userActive ? "Active" : "Not activated"}
                            </span>
                            <div className={styles.boMuted}>
                              {u.lastActiveDate
                                ? `Last seen ${new Date(u.lastActiveDate).toLocaleDateString("nb-NO")}`
                                : "Never seen"}
                            </div>
                          </td>
                          <td>
                            {companies.map(([companyId, companyName]) => (
                              <div key={companyId}>
                                <div>{companyName}</div>
                                <div
                                  className={cls(styles.boMono, styles.boMuted)}
                                >
                                  {companyId}
                                </div>
                              </div>
                            ))}
                          </td>
                          <td>
                            {u.buildings.map((b) => (
                              <div key={b.building}>
                                <a
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href={`${APP_DOMAIN()}/company/${b.companyId}/users/${u.userId}/settings?building=${encodeURIComponent(b.building)}`}
                                >
                                  {buildingNames.get(b.building) ?? b.building}
                                </a>
                                <UserRoleTags association={b} />
                              </div>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "companies" && (
        <div className={styles.boUsearch}>
          <form
            className={styles.boUsearchForm}
            onSubmit={(e) => {
              e.preventDefault();
              setSearch({ company: companySearchInput.trim() || undefined });
            }}
          >
            <div className={styles.boInputWrap} style={{ flex: 1 }}>
              <MIcon name="domain" size={18} style={{ color: "var(--fg-3)" }} />
              <input
                type="text"
                placeholder="Company name or org number (starts with)…"
                value={companySearchInput}
                onChange={(e) => setCompanySearchInput(e.target.value)}
                autoFocus
              />
              {companySearchInput && (
                <button
                  type="button"
                  className={styles.boInputClear}
                  onClick={() => {
                    setCompanySearchInput("");
                    setSearch({ company: undefined });
                  }}
                  aria-label="Clear"
                >
                  <MIcon name="close" size={16} />
                </button>
              )}
            </div>
            <button
              type="submit"
              className={cls(styles.boBtn, styles.primary)}
              disabled={companyPending || !companySearchInput}
            >
              {companyPending ? "Searching…" : "Search"}
            </button>
          </form>

          {companyResults && (
            <div className={styles.boUsearchResults}>
              <div className={styles.boUsearchResultsHead}>
                <MIcon name="domain" size={16} />
                <strong>
                  {groupedCompanies.length} compan
                  {groupedCompanies.length === 1 ? "y" : "ies"}
                </strong>
                <div style={{ flex: 1 }} />
                <button
                  type="button"
                  className={cls(styles.boBtn, styles.ghost, styles.boBtnSm)}
                  onClick={() => setSearch({ company: undefined })}
                >
                  <MIcon name="close" size={14} />
                  Clear results
                </button>
              </div>
              {groupedCompanies.length === 0 ? (
                <div className={styles.boEmpty}>No companies match.</div>
              ) : (
                <table className={styles.boUsearchTable}>
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Org nr</th>
                      <th>Company ID</th>
                      <th>Buildings</th>
                      <th>Users / admins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedCompanies.map((c) => (
                      <tr key={c.buildingTenantId}>
                        <td>
                          {c.buildingTenantName}
                          {c.sharryCompanyId && (
                            <SharryLink
                              href={`https://welcome.sharryapp.com/portalwx/company/${encodeURIComponent(c.sharryCompanyId)}/form/detail`}
                            />
                          )}
                        </td>
                        <td className={cls(styles.boMono, styles.boMuted)}>
                          {c.orgNumber}
                          {c.orgNumber && <CopyButton value={c.orgNumber} />}
                        </td>
                        <td className={cls(styles.boMono, styles.boMuted)}>
                          {c.buildingTenantId}
                          <CopyButton value={c.buildingTenantId} />
                        </td>
                        <td>
                          {c.buildings.map((b) => (
                            <div key={b.buildingId}>
                              <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href={`${APP_DOMAIN()}/company/${c.buildingTenantId}/settings?building=${encodeURIComponent(b.buildingId)}`}
                              >
                                {buildingNames.get(b.buildingId) ??
                                  b.buildingId}
                              </a>
                              <span className={styles.boMuted}>
                                {b.floor && ` · floor ${b.floor}`}
                                {` · ${b.entranceCount} entrance${b.entranceCount === 1 ? "" : "s"}`}
                              </span>
                              {b.accessControlSystemIntegration && (
                                <span
                                  className={cls(styles.boTag, styles.boTagOn)}
                                >
                                  ACS
                                </span>
                              )}
                            </div>
                          ))}
                        </td>
                        <td>
                          {c.buildings.map((b) => (
                            <div key={b.buildingId}>
                              {b.userCount ?? "–"} / {b.adminCount ?? "–"}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "buildings" && (
        <>
          <div className={styles.boSummary}>
            <div className={styles.boSummaryTotal}>
              <div className={styles.boSummaryTotalNum}>
                {filtered.length}
                {filtered.length !== all.length && (
                  <span className={styles.boSummaryTotalOf}>
                    {" "}
                    / {all.length}
                  </span>
                )}
              </div>
              <div className={styles.boSummaryTotalLabel}>Buildings</div>
            </div>
            <div className={styles.boSummaryDivider} />
            <div className={styles.boSummaryChips}>
              {FEATURES.map((f) => {
                const n = count(f.key);
                const active = activeFilters.includes(f.key as string);
                return (
                  <button
                    key={f.key as string}
                    className={cls(styles.boFchip, active && styles.isActive)}
                    style={
                      {
                        "--fc": f.color,
                        "--fcs": f.soft,
                      } as CSSProperties
                    }
                    onClick={() => toggleFilter(f.key as string)}
                    title={`Filter to buildings with ${f.name} enabled`}
                  >
                    <span className={styles.boFchipDot} />
                    <span className={styles.boFchipLabel}>{f.name}</span>
                    <span className={styles.boFchipCount}>{n}</span>
                    <span className={styles.boFchipBar}>
                      <span
                        className={styles.boFchipBarFill}
                        style={{ width: `${pct(n, filtered.length)}%` }}
                      />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.boFilterbar}>
            <div
              className={styles.boInputWrap}
              style={{ flex: 1, maxWidth: 480 }}
            >
              <MIcon name="search" size={18} style={{ color: "var(--fg-3)" }} />
              <input
                type="text"
                placeholder="Filter buildings by name or owner…"
                value={searchString}
                onChange={(e) =>
                  setSearch({ filter: e.target.value || undefined }, true)
                }
              />
              {searchString && (
                <button
                  className={styles.boInputClear}
                  onClick={() => setSearch({ filter: undefined })}
                  aria-label="Clear"
                >
                  <MIcon name="close" size={16} />
                </button>
              )}
            </div>
            {activeFilters.length > 0 && (
              <button
                className={cls(styles.boBtn, styles.ghost, styles.boBtnSm)}
                onClick={clearFilters}
              >
                <MIcon name="filter_alt_off" size={16} />
                Clear {activeFilters.length} filter
                {activeFilters.length === 1 ? "" : "s"}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <div className={styles.boSort}>
              <span className={styles.boSortLabel}>Sort</span>
              <select
                value={sort}
                onChange={(e) =>
                  setSearch({
                    sort:
                      e.target.value === "name" ? undefined : e.target.value,
                  })
                }
              >
                <option value="name">Name (A–Z)</option>
                <option value="owner">Owner</option>
                <option value="features-desc">Most features</option>
                <option value="features-asc">Fewest features</option>
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className={styles.boEmpty}>
              No buildings match your filters.
            </div>
          ) : (
            <div className={styles.boTableWrap}>
              <table className={styles.boTable}>
                <thead>
                  <tr>
                    <th>Building</th>
                    {FEATURES.map((f) => (
                      <th
                        key={f.key as string}
                        className={styles.feat}
                        title={f.name}
                      >
                        <div className={styles.boFeatHead}>
                          <span
                            className={styles.boFeatHeadDot}
                            style={{ background: f.color }}
                          />
                          <span style={{ fontWeight: 600 }}>{f.name}</span>
                        </div>
                      </th>
                    ))}
                    <th>Companies</th>
                    <th>Users</th>
                    <th>Contacts</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <BuildingRow key={b.id} b={b} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BuildingRow({ b }: { b: BuildingWithId }) {
  return (
    <tr>
      <td>
        <div>
          <a
            className={styles.boName}
            target="_blank"
            rel="noopener noreferrer"
            href={`${APP_DOMAIN()}/building/settings?building=${encodeURIComponent(b.id)}`}
          >
            {b.displayName || b.id}
          </a>
          <div className={styles.boSub}>{b.owner}</div>
        </div>
      </td>
      {FEATURES.map((f) => {
        const on = !!b[f.key];
        return (
          <td
            key={f.key as string}
            className={cls(styles.boFeatCell, on && styles.isLink)}
          >
            {on ? (
              <a
                className={styles.boFeatLink}
                target="_blank"
                rel="noopener noreferrer"
                href={`${APP_DOMAIN()}/building/${f.link}?building=${encodeURIComponent(b.id)}`}
                title={`${f.name} settings`}
              >
                <span
                  className={cls(styles.boPip, styles.on)}
                  style={{ "--fc": f.color, "--fcs": f.soft } as CSSProperties}
                >
                  <MIcon name={f.icon} size={16} fill={1} />
                </span>
              </a>
            ) : (
              <span
                className={cls(styles.boPip, styles.off)}
                title={`${f.name} not enabled`}
              >
                <MIcon name="remove" size={16} />
              </span>
            )}
          </td>
        );
      })}
      <td className={styles.boMono}>{b.companyCount ?? "–"}</td>
      <td className={styles.boMono}>{b.userCount ?? "–"}</td>
      <td>
        <div className={styles.boContacts}>
          {b.propertyManager && (
            <ContactPill user={b.propertyManager} role="Property manager" />
          )}
          {b.operationsContact && (
            <ContactPill user={b.operationsContact} role="Ops" />
          )}
        </div>
      </td>
    </tr>
  );
}

function ContactPill({ user, role }: { user: BuildingContact; role: string }) {
  const id = user.id;
  const initials = (user.firstName?.[0] ?? "") + (user.lastName?.[0] ?? "");
  return (
    <span
      className={styles.boContact}
      title={`${role}\n${user.email ?? ""}\n${user.phone ?? ""}`}
    >
      <span
        className={styles.boAvatar}
        style={{ background: avatarColor(initials || id) }}
      >
        {initials.toUpperCase()}
      </span>
      <span>
        {user.firstName} {user.lastName?.[0] ? `${user.lastName[0]}.` : ""}
      </span>
    </span>
  );
}

function SharryLink({ href }: { href: string }) {
  return (
    <a
      className={cls(styles.boTag, styles.boTagOn)}
      target="_blank"
      rel="noopener noreferrer"
      href={href}
      title="Open in Sharry portal"
    >
      Sharry
      <MIcon name="arrow_outward" size={12} />
    </a>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={styles.boCopy}
      aria-label={`Copy ${value}`}
      title="Copy"
      onClick={() =>
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
      }
    >
      <MIcon name={copied ? "check" : "content_copy"} size={14} />
    </button>
  );
}

function UserRoleTags({
  association,
}: {
  association: UserAllBuildingsMatch["buildings"][number];
}) {
  const tags = [
    association.isAdmin && "Admin",
    association.isAccessAdmin && "Access admin",
    association.integratedWithAccessControlSystem && "ACS",
    association.integratedWithWelcomeFood && "Food",
  ].filter((tag): tag is string => !!tag);
  return tags.map((tag) => (
    <span key={tag} className={cls(styles.boTag, styles.boTagOn)}>
      {tag}
    </span>
  ));
}
